import React from 'react';
import { Heart, Edit, X, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../ui/collapsible';

const LIFESTYLE_FIELDS = [
  { key: 'nutrition', label: 'Nutrition' },
  { key: 'physical_activity', label: 'Physical Activity' },
  { key: 'sleep', label: 'Sleep' },
  { key: 'substance_use', label: 'Substance Use' },
  { key: 'relationships', label: 'Relationships' },
  { key: 'stressors', label: 'Stressors' },
  { key: 'resiliency_practices', label: 'Resiliency Practices' }
];

const LifestyleSummarySection = ({
  editedData,
  isEditing,
  isCollapsed,
  onToggleEdit,
  onCancelEdit,
  onToggleCollapse,
  onUpdateField
}) => {
  return (
    <div className="mb-6">
      <Collapsible open={!isCollapsed} onOpenChange={onToggleCollapse}>
        <div className="card border-2 border-orange-200 bg-gradient-to-br from-white to-orange-50 shadow-lg">
          <div className="flex items-center justify-between mb-4 pb-2 border-b-2 border-orange-100">
            <CollapsibleTrigger asChild>
              <button className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="lifestyle-summary-collapse-btn">
                <ChevronRight
                  size={20}
                  className={`text-orange-600 transition-transform duration-200 ${!isCollapsed ? 'rotate-90' : ''}`}
                />
                <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                  <Heart size={20} className="text-orange-600" />
                  Lifestyle Summary
                </h2>
              </button>
            </CollapsibleTrigger>
            {!isEditing ? (
              <Button onClick={onToggleEdit} className="btn-secondary" data-testid="lifestyle-summary-edit-btn">
                <Edit size={16} className="mr-2" />
                Edit
              </Button>
            ) : (
              <Button onClick={onCancelEdit} className="btn-ghost text-red-600" data-testid="lifestyle-summary-cancel-btn">
                <X size={16} className="mr-2" />
                Cancel
              </Button>
            )}
          </div>

          <CollapsibleContent>
            {!isEditing ? (
              <div className="space-y-4">
                {LIFESTYLE_FIELDS.map(section => (
                  <div key={section.key} className="p-3 bg-white rounded-lg border border-orange-200">
                    <p className="text-xs font-semibold text-orange-900 uppercase tracking-wider mb-2">{section.label}</p>
                    {editedData.health_record?.[section.key] ? (
                      <p className="text-slate-700 whitespace-pre-wrap">{editedData.health_record[section.key]}</p>
                    ) : (
                      <p className="text-slate-500 italic text-sm">No data</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <Accordion type="single" collapsible className="w-full">
                {LIFESTYLE_FIELDS.map(section => (
                  <AccordionItem key={section.key} value={section.key}>
                    <AccordionTrigger>{section.label}</AccordionTrigger>
                    <AccordionContent>
                      <Textarea
                        value={editedData.health_record?.[section.key] || ''}
                        onChange={(e) => onUpdateField('health_record', section.key, e.target.value)}
                        className="w-full"
                        rows={6}
                        placeholder={`Enter ${section.label.toLowerCase()}...`}
                      />
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </CollapsibleContent>
        </div>
      </Collapsible>
    </div>
  );
};

export default LifestyleSummarySection;
